/**
 * /premium/success — polls the checkout status endpoint until the
 * subscription flips to "paid", then shows a warm confirmation and links
 * back to the dashboard.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";

export default function PremiumSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState({ status: "checking", tier: "", interval: "" });

  useEffect(() => {
    if (!sid) { setState({ status: "error", tier: "", interval: "" }); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await http.get(`/premium/checkout/status/${sid}`);
        if (cancelled) return;
        if (data?.payment_status === "paid") {
          setState({ status: "paid", tier: data.tier_name || "your plan", interval: data.interval || "" });
          return;
        }
        if (attempts >= 30) {
          setState({ status: "timeout", tier: "", interval: "" });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setState({ status: "error", tier: "", interval: "" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sid]);

  return (
    <div data-testid="premium-success" className="max-w-lg mx-auto px-6 py-24 text-center">
      {state.status === "paid" ? (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: "var(--wofy-primary)" }} />
          <h1 className="font-display text-3xl mb-2">You're a premium member.</h1>
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Welcome to <b>{state.tier}</b>{state.interval ? ` (${state.interval})` : ""}. Thank you for supporting Social Pulse.
          </p>
          <div className="mt-6 flex justify-center gap-2">
            <Link to="/dashboard" className="wofy-btn-primary">Open dashboard</Link>
            <Link to="/" className="wofy-btn-ghost">Back home</Link>
          </div>
        </>
      ) : state.status === "checking" ? (
        <>
          <Loader2 className="w-10 h-10 mx-auto animate-spin mb-4" style={{ color: "var(--wofy-primary)" }} />
          <p className="text-sm">Confirming your subscription with Stripe…</p>
        </>
      ) : (
        <>
          <Sparkles className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--wofy-primary)" }} />
          <p className="text-sm">
            We couldn't confirm your payment automatically — no worries. Your dashboard will update
            within a few minutes.
          </p>
          <Link to="/dashboard" className="wofy-btn-primary mt-4 inline-flex">Open dashboard</Link>
        </>
      )}
    </div>
  );
}
